/**
 * BusinessLayout — dashboard EMPRESARIAL (/business/*).
 * Sidebar agrupado Ola A (mocks design-mocks-business-ola-a): Inicio · Mover ·
 * Pagar · Cobrar · Tesorería · Más. Mismo patrón expand/collapse que personas
 * Sidebar.jsx; tokens fx-shell-* para glass dark/light sin romper /dashboard.
 */
import { useEffect, useState } from 'react';
import { NavLink, Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { getTheme, toggleTheme } from '../lib/theme';
import Logo from '../assets/images/Logo_blue_2x.svg';
import { useSnapshot } from 'valtio';
import { state } from '../store/global';
import { myBusinesses } from '../lib/business';

const ChevronIcon = ({ open, color = '#9CA3AF' }) => (
  <svg
    className={`w-4 h-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M6 9l6 6 6-6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** IA bloqueada — no inventar otros grupos ni rutas de menú. */
const NAV_GROUPS = [
  {
    title: 'Inicio',
    path: '/business',
    end: true,
  },
  {
    title: 'Mover',
    children: [
      { title: 'Recargar', path: '/business/recargar' },
      { title: 'Enviar', path: '/business/enviar' },
      { title: 'Cuentas globales', path: '/business/cuentas-globales' },
    ],
  },
  {
    title: 'Pagar',
    children: [
      { title: 'Pagos y nómina', path: '/business/pagos' },
      { title: 'Blockchain', path: '/business/blockchain' },
    ],
  },
  {
    title: 'Cobrar',
    children: [{ title: 'Cartera', path: '/business/cartera' }],
  },
  {
    title: 'Tesorería',
    children: [
      { title: 'Provisiones', path: '/business/provisiones' },
      { title: 'Caja', path: '/business/caja' },
      { title: 'Rendimientos', path: '/business/rendimientos' },
      { title: 'Calendario', path: '/business/calendario' },
    ],
  },
  {
    title: 'Más',
    children: [
      { title: 'Equipo', path: '/business/equipo' },
      { title: 'Historial', path: '/business/historial' },
      { title: 'Extractos', path: '/business/extractos' },
      { title: 'Integraciones', path: '/business/integraciones' },
    ],
  },
];

function isPathActive(pathname, path, end = false) {
  if (end || path === '/business') return pathname === '/business' || pathname === '/business/';
  return pathname === path || pathname.startsWith(`${path}/`);
}

function groupHasActive(pathname, item) {
  return (item.children || []).some((c) => isPathActive(pathname, c.path));
}

function BusinessNav({ pathname, openGroups, toggleGroup, onNavigate, onClose, showPersonalLink }) {
  const navigate = useNavigate();

  const go = (path) => {
    navigate(path);
    onNavigate?.(path);
    onClose?.();
  };

  return (
    <nav className="biz-side-nav flex flex-col gap-0.5 px-3 flex-1 overflow-y-auto">
      {NAV_GROUPS.map((item) => {
        if (!item.children) {
          const active = isPathActive(pathname, item.path, item.end);
          return (
            <button
              key={item.title}
              type="button"
              onClick={() => go(item.path)}
              className={`biz-nav-item px-4 py-2.5 rounded-xl text-sm text-left transition flex items-center gap-2 ${
                active ? 'biz-nav-item--on font-semibold' : 'font-normal'
              }`}
            >
              <span className={`biz-nav-dot ${active ? 'biz-nav-dot--on' : ''}`} aria-hidden="true" />
              {item.title}
            </button>
          );
        }

        const open = !!openGroups[item.title];
        const groupActive = groupHasActive(pathname, item);
        return (
          <div key={item.title} className="biz-nav-group">
            <button
              type="button"
              onClick={() => toggleGroup(item.title)}
              className={`biz-nav-item biz-nav-group-label w-full px-4 py-2.5 rounded-xl text-sm text-left transition flex items-center gap-2 ${
                groupActive ? 'biz-nav-item--group-on font-semibold' : 'font-normal'
              }`}
              aria-expanded={open}
            >
              <span className={`biz-nav-dot ${groupActive ? 'biz-nav-dot--on' : ''}`} aria-hidden="true" />
              <span className="flex-1">{item.title}</span>
              <ChevronIcon open={open} color={groupActive ? '#2A59C6' : '#9CA3AF'} />
            </button>
            {open && (
              <div className="biz-nav-children mt-0.5 mb-1 ml-3 pl-3 border-l border-[#E5E7EB] space-y-0.5">
                {item.children.map((child) => {
                  const childActive = isPathActive(pathname, child.path);
                  return (
                    <button
                      key={child.path}
                      type="button"
                      onClick={() => go(child.path)}
                      className={`biz-nav-child w-full pl-3 pr-3 py-2 rounded-lg text-[13px] text-left transition ${
                        childActive ? 'biz-nav-child--on font-semibold' : 'font-medium'
                      }`}
                    >
                      {child.title}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {showPersonalLink ? (
        <Link
          to="/dashboard"
          onClick={() => onClose?.()}
          className="biz-personal-link mt-3 mx-1 px-3 py-2 rounded-lg text-xs font-semibold text-center"
        >
          Mi cuenta personal
        </Link>
      ) : null}
    </nav>
  );
}

export default function BusinessLayout() {
  const location = useLocation();
  const pathname = location.pathname;
  const snap = useSnapshot(state);
  const [openGroups, setOpenGroups] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);
  const [tema, setTema] = useState(getTheme());
  // Solo el dueño ve el puente a Personas; operadores no (no es su cuenta personal).
  const [esDueno, setEsDueno] = useState(false);
  const onToggleTema = () => setTema(toggleTheme());

  useEffect(() => {
    let alive = true;
    myBusinesses()
      .then((list) => {
        if (!alive) return;
        const uid = String(snap.user?.id || '');
        setEsDueno((list || []).some((b) => String(b.ownerUserId) === uid));
      })
      .catch(() => { if (alive) setEsDueno(false); });
    return () => { alive = false; };
  }, [snap.user?.id]);

  useEffect(() => {
    setMobileOpen(false);
    const updates = {};
    NAV_GROUPS.forEach((item) => {
      if (item.children && groupHasActive(pathname, item)) {
        updates[item.title] = true;
      }
    });
    if (Object.keys(updates).length) {
      setOpenGroups((prev) => ({ ...prev, ...updates }));
    }
  }, [pathname]);

  const toggleGroup = (title) => {
    setOpenGroups((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  const sideInner = (
    <>
      <div className="px-6 pt-7 pb-4 shrink-0">
        <img src={Logo} alt="FlowexPay" className="w-32" />
        <span className="inline-block mt-2 text-[10px] font-bold tracking-[0.2em] uppercase text-[#0a2540] bg-[#c9a961]/20 border border-[#c9a961]/40 rounded-md px-2 py-0.5">
          Business
        </span>
      </div>
      <BusinessNav
        pathname={pathname}
        openGroups={openGroups}
        toggleGroup={toggleGroup}
        onClose={() => setMobileOpen(false)}
        showPersonalLink={esDueno}
      />
      <div className="px-6 py-4 border-t border-[#F2F4F7] shrink-0">
        <p className="text-[10px] text-[#9CA3AF] leading-snug">FlowexPay Business · control total</p>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex flex-col md:flex-row fx-shell-main">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex fx-shell-side bg-white border-r border-[#E5E7EB] w-64 min-h-screen shrink-0 flex-col">
        {sideInner}
      </aside>

      {/* Mobile drawer */}
      <button
        type="button"
        onClick={() => setMobileOpen((v) => !v)}
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' }}
        className={`md:hidden fixed z-[60] w-11 h-11 flex items-center justify-center bg-[#092F7B] hover:bg-[#0a2540] text-white shadow-lg cursor-pointer rounded-full transition-all duration-300 ${
          mobileOpen ? 'left-[13rem]' : 'left-3'
        }`}
        aria-label={mobileOpen ? 'Cerrar menú' : 'Abrir menú'}
      >
        {mobileOpen ? <X size={22} /> : <Menu size={22} />}
      </button>

      <div
        className={`
          md:hidden fixed z-40 top-0 left-0 h-full w-64 fx-shell-side bg-white border-r border-[#E9EAEB] shadow-md
          flex flex-col transition-transform duration-300
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {sideInner}
      </div>
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 bg-black/30 z-30 md:hidden"
          aria-hidden="true"
        />
      )}

      {/* Mobile horizontal strip (quick jump while drawer closed) */}
      <div className="md:hidden fx-shell-side bg-white border-b border-[#E5E7EB] overflow-x-auto">
        <div className="flex gap-1 px-3 py-2 min-w-max">
          <NavLink
            to="/business"
            end
            className={({ isActive }) =>
              `px-3 py-1.5 rounded-lg text-xs whitespace-nowrap ${
                isActive ? 'bg-[#E6EBF7] text-[#2A59C6] font-semibold' : 'text-[#475467]'
              }`
            }
          >
            Inicio
          </NavLink>
          {['/business/recargar', '/business/enviar', '/business/pagos', '/business/cartera', '/business/provisiones'].map(
            (to) => {
              const labels = {
                '/business/recargar': 'Recargar',
                '/business/enviar': 'Enviar',
                '/business/pagos': 'Pagos',
                '/business/cartera': 'Cartera',
                '/business/provisiones': 'Tesorería',
              };
              return (
                <NavLink
                  key={to}
                  to={to}
                  className={({ isActive }) =>
                    `px-3 py-1.5 rounded-lg text-xs whitespace-nowrap ${
                      isActive ? 'bg-[#E6EBF7] text-[#2A59C6] font-semibold' : 'text-[#475467]'
                    }`
                  }
                >
                  {labels[to]}
                </NavLink>
              );
            },
          )}
        </div>
      </div>

      <main className="flex-1 flex flex-col min-w-0">
        <div className="fx-shell-header bg-white border-b border-[#E5E7EB] px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="text-sm font-semibold text-[#181D27]">FlowexPay Business</div>
          <div className="flex items-center gap-3 md:gap-4">
            <button
              type="button"
              onClick={onToggleTema}
              title={tema === 'dark' ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'}
              aria-label="Cambiar tema"
              className="w-8 h-8 flex items-center justify-center rounded-full text-[#535862] hover:text-[#3d8bfd] hover:bg-[#F2F4F7] cursor-pointer"
            >
              {tema === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            {esDueno ? (
              <Link to="/dashboard" className="text-xs text-[#6B7280] hover:text-[#181D27]">
                Mi cuenta personal
              </Link>
            ) : null}
            <button
              type="button"
              onClick={() => state.logout()}
              className="text-xs font-medium text-[#F04438] hover:underline"
            >
              Cerrar sesión
            </button>
          </div>
        </div>
        <div className="px-4 md:px-8 py-6 flex-1">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
